import { useEffect, useState } from 'react'
import { leadService, type DiscountLimits } from '../../leads/services/leads'
import { adminService, type TelephonyConfig, type TelephonyProvider } from '../../identity/services/admin'
import { authService } from '../../identity/services/auth'
import { Percent, CheckCircle, AlertCircle, XCircle, Phone, ChevronDown, ChevronUp } from 'lucide-react'
import PasswordInput from '../../../components/PasswordInput'

// ── Provider-aware field metadata ─────────────────────────────────────────────

const PROVIDER_META: Record<TelephonyProvider, {
  label: string
  accountSidLabel: string
  accountSidHint: string
  apiKeyLabel: string
  apiSecretLabel: string
  virtualNumberLabel: string
  virtualNumberHint: string
  showSubdomain: boolean
  subdomainLabel: string
  subdomainPlaceholder: string
  subdomainHint: string
  showAccountSid: boolean
  showApiSecret: boolean
  showVirtualNumber: boolean
}> = {
  exotel: {
    label: 'Exotel',
    accountSidLabel: 'Account SID',
    accountSidHint: 'Found under Settings → API in your Exotel dashboard.',
    apiKeyLabel: 'API Key (AuthKey)',
    apiSecretLabel: 'API Secret (AuthToken)',
    virtualNumberLabel: 'Virtual Number',
    virtualNumberHint: 'Caller ID shown to customers (your Exotel ExoPhone).',
    showSubdomain: true,
    subdomainLabel: 'Sub Domain',
    subdomainPlaceholder: 'api.in.exotel.com',
    subdomainHint: 'Your Exotel SubDomain (e.g. api.in.exotel.com).',
    showAccountSid: true,
    showApiSecret: true,
    showVirtualNumber: true,
  },
  twilio: {
    label: 'Twilio',
    accountSidLabel: 'Account SID',
    accountSidHint: 'Found in your Twilio Console dashboard.',
    apiKeyLabel: 'Auth Token',
    apiSecretLabel: 'Auth Token (confirm)',
    virtualNumberLabel: 'Phone Number',
    virtualNumberHint: 'Twilio number shown to customers (+E.164 format).',
    showSubdomain: false,
    subdomainLabel: '',
    subdomainPlaceholder: '',
    subdomainHint: '',
    showAccountSid: true,
    showApiSecret: true,
    showVirtualNumber: true,
  },
  myoperator: {
    label: 'MyOperator',
    accountSidLabel: 'Company ID',
    accountSidHint: 'Manage → Calling APIs → Endpoint Details → Company ID.',
    apiKeyLabel: 'API Key (x-api-key)',
    apiSecretLabel: 'Secret Key',
    virtualNumberLabel: 'Virtual Number / DID',
    virtualNumberHint: 'Your DID number shown to customers.',
    showSubdomain: true,
    subdomainLabel: 'Campaign ID (public_ivr_id)',
    subdomainPlaceholder: 'e.g. 6a436371bb472557',
    subdomainHint: 'P2P Campaign ID — MyOperator panel → Outgoing → Campaign, then copy its Campaign ID.',
    showAccountSid: true,
    showApiSecret: true,
    showVirtualNumber: true,
  },
  cloudshope: {
    label: 'CloudShope (Click-to-Call)',
    accountSidLabel: '',
    accountSidHint: '',
    apiKeyLabel: 'API Token (Bearer)',
    apiSecretLabel: '',
    virtualNumberLabel: '',
    virtualNumberHint: '',
    showSubdomain: false,
    subdomainLabel: '',
    subdomainPlaceholder: '',
    subdomainHint: '',
    showAccountSid: false,
    showApiSecret: false,
    showVirtualNumber: false,
  },
  cloudshope_outbound: {
    label: 'CloudShope (Outbound Call)',
    accountSidLabel: '',
    accountSidHint: '',
    apiKeyLabel: 'API Token (Bearer)',
    apiSecretLabel: '',
    virtualNumberLabel: '',
    virtualNumberHint: '',
    showSubdomain: false,
    subdomainLabel: '',
    subdomainPlaceholder: '',
    subdomainHint: '',
    showAccountSid: false,
    showApiSecret: false,
    showVirtualNumber: false,
  },
}

// Fixed placeholders sent for fields the backend schema requires but a given
// provider doesn't actually use (hidden from the form for that provider).
const _UNUSED_ACCOUNT_SID = 'cloudshope'
const _UNUSED_VIRTUAL_NUMBER = '0000000000'

// ── Telephony Settings Section ────────────────────────────────────────────────

function TelephonySettings() {
  const tenantId = authService.getCurrentUser()?.tenant_id
  const [telephony, setTelephony] = useState<TelephonyConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [sipExpanded, setSipExpanded] = useState(true)

  const [form, setForm] = useState({
    provider: 'exotel' as TelephonyProvider,
    account_sid: '',
    subdomain: 'api.in.exotel.com',
    api_key: '',
    api_secret: '',
    virtual_number: '',
    webhook_token: '',
    call_time_limit: '3600',
    ring_timeout: '30',
    call_recording_mode: 'android_native' as 'android_native' | 'sip',
    android_oem: 'samsung' as 'samsung' | 'xiaomi' | 'realme',
  })

  useEffect(() => {
    if (!tenantId) { setLoading(false); return }
    adminService.getTelephonyConfig(tenantId)
      .then(cfg => {
        setTelephony(cfg)
        if (cfg.is_configured) {
          setForm(f => ({
            ...f,
            provider: (cfg.provider as TelephonyProvider) || 'exotel',
            account_sid: cfg.account_sid ?? '',
            subdomain: cfg.subdomain || '',
            virtual_number: cfg.virtual_number ?? '',
            call_time_limit: String(cfg.call_time_limit ?? 3600),
            ring_timeout: String(cfg.ring_timeout ?? 30),
            call_recording_mode: (cfg.call_recording_mode as 'android_native' | 'sip') || 'android_native',
            android_oem: (cfg.android_oem as 'samsung' | 'xiaomi' | 'realme') || 'samsung',
          }))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [tenantId])

  const meta = PROVIDER_META[form.provider]

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tenantId) return
    setSaving(true)
    setMsg(null)
    try {
      const cfg = await adminService.upsertTelephonyConfig(tenantId, {
        provider: form.provider,
        account_sid: meta.showAccountSid ? form.account_sid : _UNUSED_ACCOUNT_SID,
        subdomain: form.subdomain,
        api_key: form.api_key,
        api_secret: meta.showApiSecret ? form.api_secret : '',
        virtual_number: meta.showVirtualNumber ? form.virtual_number : _UNUSED_VIRTUAL_NUMBER,
        webhook_token: form.webhook_token,
        call_time_limit: Number(form.call_time_limit),
        ring_timeout: Number(form.ring_timeout),
        call_recording_mode: form.call_recording_mode,
        android_oem: form.call_recording_mode === 'android_native' ? form.android_oem : undefined,
      })
      setTelephony(cfg)
      setForm(f => ({ ...f, api_key: '', api_secret: '', webhook_token: '' }))
      setMsg({ type: 'success', text: `${meta.label} configuration saved.` })
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setMsg({ type: 'error', text: detail || 'Failed to save telephony config.' })
    } finally {
      setSaving(false)
    }
  }

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="card space-y-4">
      <div>
        <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Phone className="h-4 w-4" style={{ color: 'var(--brand-primary)' }} />
          Telephony
        </h2>
        <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
          Click-to-call credentials. The provider bridges calls between telecallers and customers — neither
          party sees the other's number. Leave secret fields blank to keep existing values.
        </p>
      </div>

      {form.call_recording_mode === 'android_native' && (
        <div className="alert-info text-xs" style={{ alignItems: 'flex-start' }}>
          <span>
            <strong>OEM native recording is on.</strong> Provider credentials below are only needed
            if <em>Smart Connect</em> is also enabled (click-to-call with native dialer fallback).
            If Smart Connect is off, these credentials are unused.
          </span>
        </div>
      )}

      {telephony?.is_configured && (
        <div className="alert-info text-sm">
          Active · provider <strong>{telephony.provider}</strong> · key …{telephony.api_key_hint}
          {PROVIDER_META[telephony.provider as TelephonyProvider]?.showVirtualNumber && ` · ${telephony.virtual_number}`}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-10 rounded" />)}
        </div>
      ) : (
        <form onSubmit={save} className="space-y-4" autoComplete="off">

          {/* ── Smart Connect / SIP credentials ──────────────────────────── */}
          <div
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              opacity: form.call_recording_mode === 'android_native' ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <button
              type="button"
              className="flex items-center justify-between w-full px-3 py-2.5"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => setSipExpanded(v => !v)}
            >
              <span className="text-sm font-medium flex items-center gap-2">
                <Phone className="h-3.5 w-3.5" style={{ color: 'var(--brand-primary)' }} />
                Smart Connect / SIP provider
                {form.call_recording_mode === 'android_native' && (
                  <span className="text-xs font-normal" style={{ color: 'var(--text-tertiary)' }}>
                    — only needed if Smart Connect is on
                  </span>
                )}
              </span>
              {sipExpanded
                ? <ChevronUp className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                : <ChevronDown className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />}
            </button>

            {sipExpanded && (
              <div className="px-3 pb-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

            {/* Provider selector — full width */}
            <div className="form-group sm:col-span-2">
              <label className="form-label">Provider</label>
              <select
                className="input"
                value={form.provider}
                onChange={e => {
                  setForm(f => ({ ...f, provider: e.target.value as TelephonyProvider }))
                }}
              >
                <option value="exotel">Exotel</option>
                <option value="twilio">Twilio</option>
                <option value="myoperator">MyOperator</option>
                <option value="cloudshope">CloudShope (Click-to-Call)</option>
                {/* CloudShope Outbound Call: implementation kept (service.py, router.py,
                    tests) but hidden from selection until confirmed ready for use. */}
                {/* <option value="cloudshope_outbound">CloudShope (Outbound Call)</option> */}
              </select>
            </div>

            {meta.showAccountSid && (
              <div className="form-group">
                <label className="form-label">{meta.accountSidLabel}</label>
                <input
                  className="input font-mono text-xs"
                  required
                  value={form.account_sid}
                  onChange={e => set('account_sid', e.target.value)}
                />
                <p className="form-hint">{meta.accountSidHint}</p>
              </div>
            )}

            {meta.showSubdomain && (
              <div className="form-group">
                <label className="form-label">{meta.subdomainLabel}</label>
                <input
                  className="input font-mono text-xs"
                  required
                  placeholder={meta.subdomainPlaceholder}
                  value={form.subdomain}
                  onChange={e => set('subdomain', e.target.value)}
                />
                <p className="form-hint">{meta.subdomainHint}</p>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{meta.apiKeyLabel}</label>
              <input
                className="input font-mono text-xs"
                required={!telephony?.is_configured}
                placeholder={telephony?.is_configured ? '(unchanged)' : 'API key'}
                value={form.api_key}
                onChange={e => set('api_key', e.target.value)}
                autoComplete="off"
              />
            </div>

            {meta.showApiSecret && (
              <div className="form-group">
                <label className="form-label">{meta.apiSecretLabel}</label>
                <PasswordInput
                  className="input font-mono text-xs"
                  required={!telephony?.is_configured}
                  placeholder={telephony?.is_configured ? '(unchanged)' : 'Secret'}
                  value={form.api_secret}
                  onChange={e => set('api_secret', e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            )}

            {meta.showVirtualNumber && (
              <div className="form-group">
                <label className="form-label">{meta.virtualNumberLabel}</label>
                <input
                  className="input"
                  required
                  placeholder="+912233445566"
                  value={form.virtual_number}
                  onChange={e => set('virtual_number', e.target.value)}
                />
                <p className="form-hint">{meta.virtualNumberHint}</p>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Webhook Token</label>
              <input
                className="input font-mono text-xs"
                required={!telephony?.is_configured}
                placeholder={telephony?.is_configured ? '(unchanged)' : 'Secret token'}
                value={form.webhook_token}
                onChange={e => set('webhook_token', e.target.value)}
                autoComplete="new-password"
              />
              <p className="form-hint">Random secret appended to the callback URL for security.</p>
            </div>

            <div className="form-group">
              <label className="form-label">
                Call Time Limit <span style={{ color: 'var(--text-tertiary)' }}>(s)</span>
              </label>
              <input
                className="input"
                type="number"
                min={60}
                max={7200}
                value={form.call_time_limit}
                onChange={e => set('call_time_limit', e.target.value)}
              />
              <p className="form-hint">Max call duration before auto-disconnect (60–7200 s).</p>
            </div>

            <div className="form-group">
              <label className="form-label">
                Ring Timeout <span style={{ color: 'var(--text-tertiary)' }}>(s)</span>
              </label>
              <input
                className="input"
                type="number"
                min={10}
                max={120}
                value={form.ring_timeout}
                onChange={e => set('ring_timeout', e.target.value)}
              />
              <p className="form-hint">Seconds to ring before treating as no-answer (10–120 s).</p>
            </div>

                </div>{/* end inner grid */}
              </div>
            )}{/* end sipExpanded */}
          </div>{/* end SIP collapsible */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* ── Android call recording ─────────────────────────────────── */}
            <div className="form-group sm:col-span-2">
              <label className="form-label">Android Call Recording Mode</label>
              <select
                className="input"
                value={form.call_recording_mode}
                onChange={e => set('call_recording_mode', e.target.value)}
              >
                <option value="android_native">OEM native recorder (Samsung / Xiaomi / Realme)</option>
                <option value="sip">SIP / not applicable</option>
              </select>
              <p className="form-hint">
                When set to OEM native, the app reads the recording file written by the
                phone's built-in recorder after each MANUAL_MOBILE call and uploads it
                for AI analysis. Choose SIP if you use a SIP dialler that captures audio
                directly.
              </p>
            </div>

            {form.call_recording_mode === 'android_native' && (
              <div className="form-group sm:col-span-2">
                <label className="form-label">OEM Manufacturer</label>
                <select
                  className="input"
                  value={form.android_oem}
                  onChange={e => set('android_oem', e.target.value)}
                >
                  <option value="samsung">Samsung (Call recordings/)</option>
                  <option value="xiaomi">Xiaomi / MIUI (MIUI/sound_recorder/call_rec/)</option>
                  <option value="realme">Realme (Recordings/Call/)</option>
                </select>
                <p className="form-hint">
                  Determines which folder the app scans for OEM-recorded files after a call.
                  Set this to match the phones your telecallers use. If the team uses mixed
                  devices, choose the most common model — MDM-pushed config is recommended
                  for mixed fleets.
                </p>
              </div>
            )}
          </div>

          {msg && (
            <div className={msg.type === 'success' ? 'alert-success' : 'alert-danger'}>
              {msg.type === 'success'
                ? <CheckCircle className="h-4 w-4 shrink-0" />
                : <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>{msg.text}</span>
              <button type="button" className="ml-auto opacity-60 hover:opacity-100" onClick={() => setMsg(null)}>
                <XCircle className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div className="flex justify-end">
            <button className="btn-primary" disabled={saving} style={{ height: 32, fontSize: 13 }}>
              {saving ? 'Saving…' : telephony?.is_configured ? 'Update Config' : 'Save Config'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

// ── Telephony Page ───────────────────────────────────────────────────────────

export function TelephonyPage() {
  return (
    <div className="space-y-6" style={{ maxWidth: 640 }}>
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Phone className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            Telephony
          </h1>
          <p className="page-subtitle">Click-to-call provider configuration</p>
        </div>
      </div>
      <TelephonySettings />
    </div>
  )
}

// ── OD Discounts Page ────────────────────────────────────────────────────────

export default function SettingsPage() {
  const [limits, setLimits]     = useState<DiscountLimits>({ telecaller: 0, team_leader: 15, insurance_head: 30 })
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [msg, setMsg]           = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    leadService.getDiscountLimits()
      .then(setLimits)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const save = async () => {
    setSaving(true)
    setMsg(null)
    try {
      const saved = await leadService.setDiscountLimits(limits)
      setLimits(saved)
      setMsg({ type: 'success', text: 'Discount limits saved.' })
    } catch {
      setMsg({ type: 'error', text: 'Failed to save. Ensure telecaller ≤ team leader ≤ insurance head.' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6" style={{ maxWidth: 640 }}>
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Percent className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            OD Discounts
          </h1>
          <p className="page-subtitle">Discount approval limits by role</p>
        </div>
      </div>

      {/* OD Discount Limits */}
      <div className="card space-y-4">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Percent className="h-4 w-4" style={{ color: 'var(--brand-primary)' }} />
            OD Discount Limits
          </h2>
          <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Maximum OD discount (%) each role can approve without escalating.
            Discounts above a role's limit automatically escalate to the next level.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="skeleton h-10 rounded" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {([
              { key: 'telecaller',     label: 'Telecaller',     hint: 'Self-approves up to this %' },
              { key: 'team_leader',    label: 'Team Leader',    hint: 'Approves TC escalations up to this %' },
              { key: 'insurance_head', label: 'Insurance Head', hint: 'Final approver — highest limit' },
            ] as const).map(({ key, label, hint }) => (
              <div key={key} className="form-group">
                <label className="form-label">
                  {label} <span style={{ color: 'var(--text-tertiary)' }}>(%)</span>
                </label>
                <input
                  type="number" min={0} max={100} step={0.5}
                  className="input"
                  value={limits[key]}
                  onChange={e => setLimits(l => ({ ...l, [key]: parseFloat(e.target.value) || 0 }))}
                />
                <p className="form-hint">{hint}</p>
              </div>
            ))}
          </div>
        )}

        {msg && (
          <div className={msg.type === 'success' ? 'alert-success' : 'alert-danger'}>
            {msg.type === 'success'
              ? <CheckCircle className="h-4 w-4 shrink-0" />
              : <AlertCircle className="h-4 w-4 shrink-0" />}
            <span>{msg.text}</span>
            <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setMsg(null)}>
              <XCircle className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <div className="flex justify-end">
          <button className="btn-primary" disabled={saving || loading} onClick={save}
            style={{ height: 32, fontSize: 13 }}>
            {saving ? 'Saving…' : 'Save Limits'}
          </button>
        </div>
      </div>
    </div>
  )
}
